import React, { memo, useEffect } from 'react';
import { Box } from '@atlaskit/primitives/compiled';
import AIContainer from '@atlassian/jira-atlassian-intelligence/src/common/ui/ai-container/index.tsx';
import { useEntryPoint } from '@atlassian/jira-entry-point/src/controllers/use-entry-point/index.tsx';
import { JiraEntryPointContainer } from '@atlassian/jira-entry-point-container/src/index.tsx';
import { fireTrackAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import { componentWithFG } from '@atlassian/jira-feature-gate-component';
import { fg } from '@atlassian/jira-feature-gating';
import { expVal } from '@atlassian/jira-feature-experiments';
import {
	useIsReadyForProactive,
	useDebuggerStatus,
	useJqlQuery,
	useJqlEditorOnSearch,
	useJqlDebuggerStoreActions,
	useJqlErrors,
} from '../../controllers/jql-debugger-data/index.tsx';
import { ErrorSection } from '../error-section/index.tsx';
import { SuggestedFixContentEntrypoint } from '../suggested-fix-content/entrypoint.tsx';
import {
	ERROR_BOUNDARY_IDS,
	PACKAGE_NAME_JQL_DEBUGGER,
	TEAM_NAME,
} from '../../common/constants.tsx';
import { onClick, preloadApiCall, preloadApiCallNew } from '../fix-error-button/on-click.tsx';
import { getAiAttributes } from '../../common/get-ai-attributes.tsx';

function OnError() {
	const debuggerActions = useJqlDebuggerStoreActions();

	useEffect(() => {
		// On error, set the feature state to be JS_ERROR, so that such scenarios can be handled without
		// interfering with the feature initial state nor with the AI event instrumentation
		debuggerActions.setStatus('JS_ERROR');
	}, [debuggerActions]);

	return null;
}

const JQLDebuggerCommonEntryComponent = (): React.JSX.Element => {
	const [status] = useDebuggerStatus();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const debuggerStoreActions = useJqlDebuggerStoreActions();
	const [query] = useJqlQuery();
	const [jqlErrors] = useJqlErrors();
	const [onSearch_] = useJqlEditorOnSearch();
	const [isReadyForProactive] = useIsReadyForProactive();

	const { entryPointReferenceSubject, entryPointActions } = useEntryPoint(
		SuggestedFixContentEntrypoint,
		{},
	);

	useEffect(() => {
		if (fg('jql_fixer_analytics_fixes')) {
			fireTrackAnalytics(
				createAnalyticsEvent({}),
				'aiExperience_jqlFixer viewed',
				getAiAttributes({}),
			);
		}
	}, [query, createAnalyticsEvent]);

	const onSearch = fg('jql_fixer_analytics_fixes') ? undefined : onSearch_;

	// For the proactive experience, we call the API through a useEffect for each applicable render
	useEffect(() => {
		if (status === 'INIT') {
			if (!isReadyForProactive) {
				if (fg('jql-fixer-cost-reduction-targeting-gate')) {
					// Preload the API call only when the experiment variant is CONTROL
					if (!expVal('jql_fixer_cost_reduction', 'isEnabled', false)) {
						preloadApiCallNew(createAnalyticsEvent, query, jqlErrors);
					}
				} else {
					preloadApiCall(query, jqlErrors);
				}
			} else {
				onClick({
					testId: 'jql-debugger-common-entry',
					createAnalyticsEvent,
					debuggerStoreActions,
					query,
					jqlErrors,
					onSearch,
					isReadyForProactive,
				});
			}
		}
	}, [
		isReadyForProactive,
		status,
		createAnalyticsEvent,
		debuggerStoreActions,
		jqlErrors,
		onSearch,
		query,
	]);

	/*
		We load the entrypoint conditionally here as the JQL debugger panel is rendered underneath the JQL builder for large screen sizes,
		and across the issue view for smaller screen sizes - their visibilty is controlled by a <Show>, but they are two separately
		rendered components. As a result, if we are switching from one screen size or issue view to another, the newly rendered version of the
		JQL debugger will not have a loaded entry point, so we load it here to ensure the content is still visible across different views.

		Example of behaviour without this conditional:
		https://www.loom.com/share/725438e2ea16481591bbe510815e9ca3?sid=b3dc286e-9c37-4cf9-a2c9-f55d3561a166
	*/
	if (status !== 'INIT') {
		entryPointActions?.load();
	}

	return (
		<AIContainer
			spacing="none"
			hasNewBorderExperience
			shouldFitContainer
			isLoading={status === 'LOADING_RESULT'}
		>
			<Box padding="space.150">
				<ErrorSection entryPointActions={entryPointActions} />
			</Box>
			<JiraEntryPointContainer
				entryPointReferenceSubject={entryPointReferenceSubject}
				runtimeProps={{}}
				packageName={PACKAGE_NAME_JQL_DEBUGGER}
				id={ERROR_BOUNDARY_IDS.FIX_WITH_AI_MODAL}
				teamName={TEAM_NAME}
				fallback={null}
				errorFallback={OnError}
			/>
		</AIContainer>
	);
};

const JQLDebuggerCommonEntryMemoized = memo(JQLDebuggerCommonEntryComponent);

export const JQLDebuggerCommonEntry = componentWithFG(
	'jql_fixer_analytics_fixes',
	JQLDebuggerCommonEntryMemoized,
	JQLDebuggerCommonEntryComponent,
);
